import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const clamp = (value, lo, hi) => Math.max(lo, Math.min(hi, value))

/**
 * PinchZoomA4Stage
 *
 * Wraps an A4-sized prescription preview and presents it as a pannable + zoomable
 * stage so it reads correctly on narrow (mobile) screens instead of being squeezed
 * into the viewport width (which collapses tables to one-letter-per-line and clips
 * the right edge).
 *
 * Key constraint: the preview's pagination engine measures elements with
 * getBoundingClientRect(), which is corrupted by an ancestor CSS transform. So we
 * keep the children at their natural A4 width and only switch on the display
 * transform AFTER the engine has settled (settleDelayMs) — and we never trigger a
 * re-measure (window resize / dimension refresh) from zoom/pan, so the settled
 * layout stays intact while the user reads.
 */
export default function PinchZoomA4Stage({
  contentWidth = 794,
  maxZoom = 4,
  settleQuietMs = 500,
  settleMaxWaitMs = 9000,
  scrollRef,
  children,
}) {
  const viewportRef = useRef(null)
  const contentRef = useRef(null)
  const pinchRef = useRef(null)
  const lastTapRef = useRef(0)
  const userZoomedRef = useRef(false)
  const enableTimerRef = useRef(null)

  // The scroll viewport is also the element the drawer's "scroll through to render all
  // pages" routine drives, so expose it to the parent via scrollRef.
  const setViewportNode = useCallback(
    (node) => {
      viewportRef.current = node
      if (typeof scrollRef === 'function') scrollRef(node)
      else if (scrollRef) scrollRef.current = node
    },
    [scrollRef],
  )

  const [fitZoom, setFitZoom] = useState(1)
  const [zoom, setZoom] = useState(null) // null until the engine settles → render at 1:1 meanwhile
  const [naturalHeight, setNaturalHeight] = useState(0)
  const [scaleEnabled, setScaleEnabled] = useState(false)
  const [viewportHeight, setViewportHeight] = useState(0)

  // The drawer doesn't impose a bounded height on its content, so height:100% collapses to
  // content height (the whole drawer would scroll and the controls would sit off-screen).
  // Pin the stage to the space between its top and the bottom of the screen so it becomes a
  // proper bounded pan/zoom viewport with the controls anchored to it.
  useLayoutEffect(() => {
    const measureHeight = () => {
      const viewport = viewportRef.current
      if (!viewport) return
      const top = viewport.getBoundingClientRect().top
      const available = Math.max(240, window.innerHeight - top)
      setViewportHeight(available)
    }
    measureHeight()
    const raf = window.requestAnimationFrame(measureHeight)
    const timer = window.setTimeout(measureHeight, 350) // after the drawer open animation
    window.addEventListener('resize', measureHeight)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(timer)
      window.removeEventListener('resize', measureHeight)
    }
  }, [])

  // Compute the fit-to-width factor from the live viewport width.
  const recomputeFit = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const available = viewport.clientWidth
    if (!available) return
    const next = clamp(available / contentWidth, 0.2, 1)
    setFitZoom(next)
    if (!userZoomedRef.current) {
      setZoom(next)
    }
  }, [contentWidth])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return undefined
    recomputeFit()
    const ro = new ResizeObserver(recomputeFit)
    ro.observe(viewport)
    return () => ro.disconnect()
  }, [recomputeFit])

  // The visual scale MUST NOT be applied while the pagination engine is still measuring:
  // the engine reads element heights with getBoundingClientRect, which a CSS transform
  // scales — making it think content is smaller/larger and breaking the page into the
  // wrong number of pages. So we keep the content at 1:1 until the layout has been
  // QUIET (content height unchanged) for settleQuietMs, i.e. pagination has settled.
  const scheduleEnable = useCallback(
    (delay) => {
      if (enableTimerRef.current) window.clearTimeout(enableTimerRef.current)
      enableTimerRef.current = window.setTimeout(() => setScaleEnabled(true), delay)
    },
    [],
  )

  // Safety net: apply the scale eventually even if the layout never goes fully quiet.
  useEffect(() => {
    const fallback = window.setTimeout(() => setScaleEnabled(true), settleMaxWaitMs)
    return () => window.clearTimeout(fallback)
  }, [settleMaxWaitMs])

  // Track the natural (unscaled) content height. Every height change means pagination is
  // still in flux → re-arm the "quiet" timer so the scale only switches on once stable.
  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return undefined
    let lastHeight = -1
    const measure = () => {
      const h = el.scrollHeight || el.offsetHeight || 0
      setNaturalHeight(h)
      if (h !== lastHeight) {
        lastHeight = h
        scheduleEnable(settleQuietMs)
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [scheduleEnable, settleQuietMs])

  // A window resize re-triggers the engine's getBoundingClientRect measurement, so drop
  // back to 1:1 first, let it re-paginate cleanly, then re-apply the scale once quiet.
  useEffect(() => {
    const onResize = () => {
      setScaleEnabled(false)
      scheduleEnable(settleQuietMs)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (enableTimerRef.current) window.clearTimeout(enableTimerRef.current)
    }
  }, [scheduleEnable, settleQuietMs])

  const effectiveZoom = scaleEnabled ? zoom ?? fitZoom : 1
  const minZoom = Math.min(fitZoom, 1)

  const touchDistance = (touches) =>
    Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY)
  const touchMidpoint = (touches) => ({
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  })

  const onTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 2) {
        const viewport = viewportRef.current
        const rect = viewport.getBoundingClientRect()
        const mid = touchMidpoint(e.touches)
        const midX = mid.x - rect.left
        const midY = mid.y - rect.top
        pinchRef.current = {
          startDist: touchDistance(e.touches),
          startZoom: effectiveZoom,
          anchorX: (viewport.scrollLeft + midX) / effectiveZoom,
          anchorY: (viewport.scrollTop + midY) / effectiveZoom,
          midX,
          midY,
        }
      }
    },
    [effectiveZoom],
  )

  // Non-passive so we can preventDefault the two-finger gesture.
  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return undefined
    const handleMove = (e) => {
      const pinch = pinchRef.current
      if (e.touches.length === 2 && pinch) {
        e.preventDefault()
        const next = clamp(
          pinch.startZoom * (touchDistance(e.touches) / pinch.startDist),
          minZoom,
          maxZoom,
        )
        userZoomedRef.current = true
        setScaleEnabled(true)
        setZoom(next)
        viewport.scrollLeft = pinch.anchorX * next - pinch.midX
        viewport.scrollTop = pinch.anchorY * next - pinch.midY
      }
    }
    viewport.addEventListener('touchmove', handleMove, { passive: false })
    return () => viewport.removeEventListener('touchmove', handleMove)
  }, [minZoom, maxZoom])

  const onTouchEnd = useCallback(
    (e) => {
      if (e.touches.length < 2) pinchRef.current = null
      // Double-tap toggles between fit-to-width and a readable zoom.
      if (e.touches.length === 0 && e.changedTouches.length === 1) {
        const now = Date.now()
        if (now - lastTapRef.current < 300) {
          userZoomedRef.current = true
          setScaleEnabled(true)
          setZoom((current) => {
            const cur = current ?? fitZoom
            return cur > fitZoom + 0.05 ? fitZoom : clamp(Math.max(1, fitZoom * 2), minZoom, maxZoom)
          })
          lastTapRef.current = 0
        } else {
          lastTapRef.current = now
        }
      }
    },
    [fitZoom, minZoom, maxZoom],
  )

  const setZoomTo = useCallback(
    (next) => {
      userZoomedRef.current = true
      setScaleEnabled(true)
      setZoom(clamp(next, minZoom, maxZoom))
    },
    [minZoom, maxZoom],
  )

  const zoomBy = useCallback(
    (delta) => {
      userZoomedRef.current = true
      setScaleEnabled(true)
      setZoom((current) => clamp((current ?? fitZoom) + delta, minZoom, maxZoom))
    },
    [fitZoom, minZoom, maxZoom],
  )

  const zoomPercent = Math.round(effectiveZoom * 100)

  const controlButtonStyle = {
    border: 'none',
    background: 'transparent',
    color: '#1f2937',
    fontSize: '18px',
    fontWeight: 600,
    width: '34px',
    height: '34px',
    lineHeight: '34px',
    cursor: 'pointer',
    padding: 0,
  }

  const stageHeight = viewportHeight ? `${viewportHeight}px` : '100%'

  return (
    <div style={{ position: 'relative', width: '100%', height: stageHeight }}>
      {/* Force the A4 chain back to its natural width inside this stage. The preview's
          responsive CSS squeezes pages to width:100% on small screens; because those rules
          are relative, anchoring the wrapper at the true A4 width makes the whole cascade
          resolve to A4 again (and keeps tables from collapsing to one-letter-per-line). */}
      <style>
        {`
          .pinch-zoom-a4-content,
          .pinch-zoom-a4-content .prescription-page,
          .pinch-zoom-a4-content .preview-page-container,
          .pinch-zoom-a4-content [class*="A4Page"],
          .pinch-zoom-a4-content [class*="A4PreviewWrapper"],
          .pinch-zoom-a4-content [class*="A4PageMeasureComponentWrapperContainer"] {
            width: ${contentWidth}px !important;
            min-width: ${contentWidth}px !important;
            max-width: ${contentWidth}px !important;
            box-sizing: border-box !important;
          }
          .pinch-zoom-a4-content table {
            table-layout: auto !important;
          }
          @keyframes pinch-zoom-a4-spin { to { transform: rotate(360deg); } }
        `}
      </style>
      <div
        ref={setViewportNode}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{
          width: '100%',
          height: stageHeight,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-x pan-y',
          background: '#f5f5f5',
        }}
      >
        <div
          style={{
            width: contentWidth * effectiveZoom,
            height: (naturalHeight || 1) * effectiveZoom,
            position: 'relative',
            margin: '0 auto',
          }}
        >
          <div
            ref={contentRef}
            className="pinch-zoom-a4-content"
            style={{
              width: `${contentWidth}px`,
              transform: `scale(${effectiveZoom})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            {children}
          </div>
        </div>
      </div>

      {/* While pagination is still settling we render at 1:1 (so measurement stays correct);
          cover that churn with a clean loading veil until the fit-to-width view is ready. */}
      {!scaleEnabled && (
        <div
          className="hide-on-print-only"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            background: '#f5f5f5',
            zIndex: 4,
          }}
        >
          <div
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '3px solid rgba(103, 121, 227, 0.25)',
              borderTopColor: '#6779e3',
              animation: 'pinch-zoom-a4-spin 0.8s linear infinite',
            }}
          />
          <div style={{ color: '#6b7280', fontSize: '13px' }}>Preparing preview…</div>
        </div>
      )}

      {/* Zoom controls */}
      {scaleEnabled && (
        <div
          className="hide-on-print-only"
          style={{
            position: 'absolute',
            right: '12px',
            bottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            padding: '2px 4px',
            borderRadius: '999px',
            background: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.18)',
            backdropFilter: 'blur(6px)',
            zIndex: 5,
          }}
        >
          <button
            type="button"
            aria-label="Zoom out"
            style={controlButtonStyle}
            onClick={() => zoomBy(-0.25)}
          >
            −
          </button>
          <button
            type="button"
            aria-label="Fit to width"
            style={{ ...controlButtonStyle, width: 'auto', padding: '0 8px', fontSize: '13px' }}
            onClick={() => setZoomTo(fitZoom)}
          >
            {zoomPercent}%
          </button>
          <button
            type="button"
            aria-label="Zoom in"
            style={controlButtonStyle}
            onClick={() => zoomBy(0.25)}
          >
            +
          </button>
        </div>
      )}
    </div>
  )
}
